import { Head, router } from "@inertiajs/react"
import { useMemo } from "react"

import { HeadingSmall } from "@/components/heading-small"
import { PageHeader } from "@/components/page-header"
import { RaceShield } from "@/components/race-shield"
import { TraitBadge } from "@/components/trait-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { getRaceTraits } from "@/lib/race-traits"
import { cn } from "@/lib/utils"
import { leaguePath, newLeagueTeamPath } from "@/routes"
import type { BreadcrumbItem, RaceCard, RaceSelectionProps } from "@/types"

const TIER_INFO: Record<number, { title: string; description: string }> = {
  1: {
    title: "Tier 1 — Powerhouses",
    description:
      "The strongest teams in the game. Deep rosters, versatile skills, and few weaknesses. A great choice for new coaches.",
  },
  2: {
    title: "Tier 2 — Competitive",
    description:
      "Solid teams that can hold their own against anyone. They have clear strengths but need smart play to cover their gaps.",
  },
  3: {
    title: "Tier 3 — Specialists",
    description:
      "Niche teams that excel at one thing but struggle in other areas. Rewarding for coaches who commit to their playstyle.",
  },
  4: {
    title: "Tier 4 — Underdogs",
    description:
      "The stunty and chaotic teams. Unreliable, hilarious, and deeply satisfying when they pull off a win.",
  },
}

interface RaceCardComponentProps {
  race: RaceCard
  takenBy: string | null
  onSelect: () => void
}

function RaceCardComponent({
  race,
  takenBy,
  onSelect,
}: RaceCardComponentProps) {
  const isTaken = takenBy !== null
  const traits = getRaceTraits(race.key)

  return (
    <Card
      className={cn(
        "py-4 transition-[box-shadow,transform] duration-200",
        isTaken && "opacity-45",
      )}
    >
      <CardContent className="flex items-start gap-3">
        <RaceShield race={race.key} size="md" className="shrink-0" />
        <div className="min-w-0 flex-1">
          <h3
            className="font-display text-lg leading-tight font-bold"
            style={{ textShadow: "0 1px 2px oklch(0 0 0 / 0.3)" }}
          >
            {race.name}
          </h3>
          <p className="text-muted-foreground mt-1 line-clamp-2 text-xs leading-relaxed">
            {race.description}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {traits.map((trait) => (
              <TraitBadge key={trait} trait={trait} />
            ))}
          </div>
        </div>
      </CardContent>
      <CardContent className="flex justify-end pt-0">
        {isTaken ? (
          <span className="font-display text-muted-foreground text-xs font-bold tracking-wider uppercase">
            Taken by {takenBy}
          </span>
        ) : (
          <Button size="sm" onClick={onSelect}>
            Select →
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

export default function RaceSelection({
  league,
  races,
  taken_races,
}: RaceSelectionProps) {
  const tiers = useMemo(() => {
    const grouped = new Map<number, RaceCard[]>()
    for (const race of races) {
      const tier = race.tier ?? 2
      const list = grouped.get(tier) ?? []
      list.push(race)
      grouped.set(tier, list)
    }
    return Array.from(grouped.entries()).sort(([a], [b]) => a - b)
  }, [races])

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: league.name,
      href: leaguePath(league.id),
      leagueSwitcher: true,
    },
    { title: "Build Team" },
  ]

  function handleSelect(race: RaceCard) {
    router.get(newLeagueTeamPath(league.id, { race: race.key }))
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Build Team · ${league.name}`} />

      <div className="mx-auto w-full max-w-7xl px-4 py-8">
        <PageHeader
          title="Choose Your Race"
          description="Select the Blood Bowl race you will coach this season. Each race has a unique playstyle, strengths, and weaknesses."
        />

        <div className="space-y-12">
          {tiers.map(([tier, tierRaces]) => {
            const info = TIER_INFO[tier]
            return (
              <section key={tier}>
                {info && (
                  <div className="mb-6">
                    <HeadingSmall
                      title={info.title}
                      description={info.description}
                    />
                  </div>
                )}
                <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
                  {tierRaces.map((race) => (
                    <RaceCardComponent
                      key={race.key}
                      race={race}
                      takenBy={taken_races[race.key] ?? null}
                      onSelect={() => handleSelect(race)}
                    />
                  ))}
                </div>
              </section>
            )
          })}
        </div>
      </div>
    </AppLayout>
  )
}
